import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import { createFirebaseUserService } from "@/core/services/factory";
import type { FirebaseUser } from "@/core/services/types/firebase-user.types";
import { useTranslations } from "@/lib/useTranslations";

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function UsersPanel() {
  const t = useTranslations("users");
  const usersService = useMemo(() => createFirebaseUserService(), []);

  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [users, setUsers] = useState<FirebaseUser[]>([]);
  const [total, setTotal] = useState(0);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (mode: "replace" | "append", pageToken?: string | null) => {
      if (mode === "replace") setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const result = await usersService.list({
          q: submittedQuery || undefined,
          pageToken: pageToken ?? null,
          pageSize: 50,
        });
        setUsers((prev) =>
          mode === "append" ? [...prev, ...result.users] : result.users,
        );
        setNextPageToken(result.nextPageToken);
        setTotal(result.total);
      } catch {
        setError(t("loadError"));
        if (mode === "replace") {
          setUsers([]);
          setTotal(0);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [submittedQuery, t, usersService],
  );

  useEffect(() => {
    void load("replace");
  }, [load]);

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    setSubmittedQuery(query.trim());
  };

  return (
    <div className="space-y-4">
      <section aria-label={t("totalLabel")} className="grid gap-4 sm:grid-cols-3">
        <article className="rounded-xl border border-black/10 bg-white p-4 shadow-sm">
          <p className="text-sm text-neutral-600">{t("totalLabel")}</p>
          <p className="mt-1 text-3xl font-semibold tracking-tight text-whart-text">
            {loading && total === 0 ? "…" : total.toLocaleString("fr-FR")}
          </p>
        </article>
      </section>

      <form
        onSubmit={onSearch}
        className="flex flex-wrap items-end gap-3 rounded-xl border border-black/10 bg-white p-4 shadow-sm"
      >
        <label className="min-w-[16rem] flex-1 space-y-1.5">
          <span className="text-xs uppercase tracking-wider text-neutral-500">
            {t("searchLabel")}
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("searchPlaceholder")}
            className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-whart-text outline-none focus:border-black/20"
          />
        </label>
        <button
          type="submit"
          className="rounded-xl bg-yellow-100 px-4 py-2 text-sm font-medium text-zinc-900 transition hover:bg-amber-100"
        >
          {t("search")}
        </button>
        {submittedQuery && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setSubmittedQuery("");
            }}
            className="rounded-xl border border-black/10 px-4 py-2 text-sm text-neutral-600 transition hover:bg-neutral-50"
          >
            {t("clearSearch")}
          </button>
        )}
      </form>

      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-black/10 px-4 py-3">
          <p className="text-sm text-neutral-600">
            {t("resultCount", {
              count: submittedQuery ? users.length : total,
            })}
          </p>
        </div>

        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-neutral-500">
            {t("loading")}
          </div>
        ) : users.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-neutral-500">
            {t("empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-black/10 bg-zinc-50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-4 py-3 font-medium">{t("columns.email")}</th>
                  <th className="px-4 py-3 font-medium">{t("columns.name")}</th>
                  <th className="px-4 py-3 font-medium">
                    {t("columns.createdAt")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("columns.newsletter")}
                  </th>
                  <th className="px-4 py-3 font-medium">Notifications</th>
                  <th className="px-4 py-3 font-medium">{t("columns.admin")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-black/5 last:border-0"
                  >
                    <td className="px-4 py-3 text-whart-text">
                      <div className="font-medium">
                        {user.email.trim() ? user.email : t("guest")}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      {user.display_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {formatDate(user.created_time)}
                    </td>
                    <td className="px-4 py-3">
                      {user.newsletter ? (
                        <span className="rounded-xl bg-whart-published/15 px-2 py-0.5 text-xs text-whart-published">
                          {t("yes")}
                        </span>
                      ) : (
                        <span className="text-xs text-neutral-400">{t("no")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{user.notifyAll ? t("yes") : t("no")}</td>
                    <td className="px-4 py-3">
                      {user.isAdmin ? (
                        <span className="rounded-xl bg-amber-100 px-2 py-0.5 text-xs text-zinc-900">
                          {t("yes")}
                        </span>
                      ) : (
                        <span className="text-xs text-neutral-400">{t("no")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {nextPageToken && !loading && (
          <div className="border-t border-black/10 px-4 py-3">
            <button
              type="button"
              disabled={loadingMore}
              onClick={() => void load("append", nextPageToken)}
              className="rounded-xl border border-black/10 px-4 py-2 text-sm text-neutral-700 transition hover:bg-neutral-50 disabled:opacity-50"
            >
              {loadingMore ? t("loadingMore") : t("loadMore")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
